/**
 * Analytics helper · Phase 6.2.
 *
 * Wraps `window.gtag` so callers don't have to defensively check for it.
 * Also exposes consent-mode helpers that flip GA4 / Ads cookies on/off
 * when the user makes a decision via the cookie banner.
 *
 * Naming convention for events: snake_case verb_noun
 *   view_story, view_podcast_episode, chat_opened, chat_message_sent,
 *   chat_lead_captured, form_submit, sponsor_band_click,
 *   featured_partner_click, event_rsvp, outbound_click.
 */

const STORAGE_KEY = "mm_cookie_consent";
const TIMESTAMP_KEY = "mm_cookie_consent_at";
const AUTO_GRANT_DAYS = 7;

function safeGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* noop */
  }
}

export function getConsent() {
  if (typeof window === "undefined") return null;
  const stored = safeGet(STORAGE_KEY);
  if (stored === "granted" || stored === "denied") return stored;
  // Auto-grant after AUTO_GRANT_DAYS of inaction so legacy returning
  // visitors get analytics turned on without being banner-bombed.
  const ts = parseInt(safeGet(TIMESTAMP_KEY) || "0", 10);
  if (ts && Date.now() - ts > AUTO_GRANT_DAYS * 24 * 60 * 60 * 1000) {
    setConsent("granted", { silent: true });
    return "granted";
  }
  return null;
}

export function setConsent(value, { silent = false } = {}) {
  if (value !== "granted" && value !== "denied") return;
  safeSet(STORAGE_KEY, value);
  safeSet(TIMESTAMP_KEY, String(Date.now()));
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  // Banner es informativo: analytics_storage permanece 'granted' por defecto
  // y solo se revoca al pulsar "Rechazar". Los flags de ads/marketing solo
  // se activan cuando el usuario pulsa "Aceptar".
  window.gtag("consent", "update", {
    analytics_storage: value === "denied" ? "denied" : "granted",
    ad_storage: value === "granted" ? "granted" : "denied",
    ad_user_data: value === "granted" ? "granted" : "denied",
    ad_personalization: value === "granted" ? "granted" : "denied",
  });
  if (!silent && value === "granted") {
    // Re-fire pageview now that consent is granted so we don't lose this visit.
    window.gtag("event", "page_view", { send_to: process.env.REACT_APP_GA_ID });
  }
}

export function recordFirstTouch() {
  if (typeof window === "undefined") return;
  if (!safeGet(TIMESTAMP_KEY)) safeSet(TIMESTAMP_KEY, String(Date.now()));
}

/**
 * Fire a GA4 / GTM dataLayer event.
 *
 * @param {string} name    Event name (snake_case)
 * @param {object} params  Event params (will be flattened into GA4 payload)
 */
export function trackEvent(name, params) {
  if (typeof window === "undefined") return;
  // Always push to dataLayer so GTM tags still see the event even if GA4
  // is not loaded yet (or consent is denied).
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: name, ...(params || {}) });
  }
  if (typeof window.gtag === "function") {
    window.gtag("event", name, params || {});
  }
}

/** Convenience shortcut for outbound clicks. */
export function trackOutbound(destination, extra = {}) {
  trackEvent("outbound_click", { destination, ...extra });
}
